import type { Parcel } from "./types";

export const $ = <T extends HTMLElement = HTMLElement>(sel: string, root: ParentNode = document) => root.querySelector(sel) as T;

export function h<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Record<string, any> = {}, ...kids: (Node | string | null | undefined | false)[]) {
  const n = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null || v === false) continue;
    if (k === "class") n.className = v;
    else if (k === "html") n.innerHTML = v;
    else if (k.startsWith("on") && typeof v === "function") n.addEventListener(k.slice(2), v);
    else if (k in n && typeof v !== "string") (n as any)[k] = v;
    else n.setAttribute(k, v === true ? "" : String(v));
  }
  for (const c of kids) if (c != null && c !== false) n.append(c);
  return n;
}

let toastTimer = 0;
export function toast(msg: string, ms = 2800) {
  const t = $("#toast");
  t.textContent = msg; t.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => { t.hidden = true; }, ms);
}

export const uuid = () => crypto.randomUUID();
export const norm = (s: unknown) => (s == null ? "" : String(s)).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

export const DRUHY: [string, string][] = [
  ["Jabloň", "jablon"], ["Hrušeň", "hrusen"], ["Třešeň", "tresen"], ["Višeň", "visen"],
  ["Slivoň", "slivon"], ["Švestka", "slivon"], ["Mirabelka", "slivon"], ["Renklóda", "slivon"],
  ["Meruňka", "merunka"], ["Broskvoň", "broskvon"], ["Nektarinka", "broskvon"], ["Ořešák", "oresak"],
  ["Líska", "oresak"], ["Kaštanovník", "oresak"], ["Kdouloň", "kdoulon"], ["Mišpule", "kdoulon"],
  ["Rybíz", "drobne"], ["Angrešt", "drobne"], ["Borůvka", "drobne"], ["Malina", "drobne"],
  ["Ostružina", "drobne"], ["Josta", "drobne"], ["Muchovník", "drobne"], ["Zimolez kamčatský", "drobne"],
  ["Moruše", "jine"], ["Jeřáb", "jine"], ["Rakytník", "jine"], ["Dřín", "jine"], ["Aronie", "jine"], ["Okrasná rostlina", "jine"],
];
export const PODNOZE = ["M9", "M26", "MM106", "MM111", "A2", "semenáč", "kdouloň MA", "kdouloň MC", "planá hrušeň", "Gisela 5", "Gisela 6", "ptačí třešeň", "mahalebka", "St. Julien A", "myrobalán", "Wavit", "vlastní kořeny"];

/** Latin name from Pl@ntNet -> our Czech species name */
const LATIN: [RegExp, string][] = [
  [/^Malus/, "Jabloň"], [/^Pyrus/, "Hrušeň"], [/^Prunus avium/, "Třešeň"], [/^Prunus cerasus/, "Višeň"],
  [/^Prunus domestica/, "Slivoň"], [/^Prunus (insititia|cerasifera)/, "Mirabelka"], [/^Prunus armeniaca/, "Meruňka"],
  [/^Prunus persica/, "Broskvoň"], [/^Juglans/, "Ořešák"], [/^Corylus/, "Líska"], [/^Castanea/, "Kaštanovník"],
  [/^Cydonia/, "Kdouloň"], [/^Mespilus/, "Mišpule"], [/^Ribes uva-crispa/, "Angrešt"], [/^Ribes/, "Rybíz"],
  [/^Vaccinium/, "Borůvka"], [/^Rubus idaeus/, "Malina"], [/^Rubus/, "Ostružina"], [/^Amelanchier/, "Muchovník"],
  [/^Lonicera caerulea/, "Zimolez kamčatský"], [/^Morus/, "Moruše"], [/^Sorbus/, "Jeřáb"], [/^Hippophae/, "Rakytník"],
  [/^Cornus mas/, "Dřín"], [/^Aronia/, "Aronie"],
];
export function druhFromLatin(latin: string, names: string[]) {
  for (const [re, cz] of LATIN) if (re.test(latin)) return cz;
  const n = names[0];
  return n ? n.charAt(0).toUpperCase() + n.slice(1) : latin;
}

export function spKey(druh: string) {
  const n = norm(druh).trim();
  if (!n) return "nezname";
  const hit = DRUHY.find(([name]) => norm(name) === n) || DRUHY.find(([name]) => n.startsWith(norm(name).slice(0, 4)));
  return hit ? hit[1] : "jine";
}
const spCache: Record<string, string> = {};
export function spColor(druh: string) {
  const k = spKey(druh);
  if (!spCache[k]) spCache[k] = getComputedStyle(document.documentElement).getPropertyValue("--sp-" + k).trim() || "#888";
  return spCache[k];
}

function inRing(lat: number, lng: number, ring: number[][]) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0], yi = ring[i][1], xj = ring[j][0], yj = ring[j][1];
    if (((yi > lat) !== (yj > lat)) && (lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi)) inside = !inside;
  }
  return inside;
}
/** GeoJSON (lng,lat) polygons */
export function parcelAt(parcels: Parcel[], lat: number, lng: number) {
  for (const p of parcels) {
    const polys: number[][][][] = p.geometry.type === "Polygon" ? [p.geometry.coordinates] : p.geometry.coordinates;
    for (const poly of polys) if (inRing(lat, lng, poly[0]) && !poly.slice(1).some((hole) => inRing(lat, lng, hole))) return p;
  }
  return null;
}

export async function compressImageWithSize(file: Blob, max: number, quality = 0.82): Promise<{ blob: Blob; width: number; height: number }> {
  let src: ImageBitmap | HTMLImageElement;
  try { src = await createImageBitmap(file, { imageOrientation: "from-image" } as ImageBitmapOptions); }
  catch {
    src = await new Promise<HTMLImageElement>((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = URL.createObjectURL(file); });
  }
  const w = src.width, hgt = src.height, sc = Math.min(1, max / Math.max(w, hgt));
  const c = document.createElement("canvas");
  c.width = Math.round(w * sc); c.height = Math.round(hgt * sc);
  c.getContext("2d")!.drawImage(src, 0, 0, c.width, c.height);
  const blob = await new Promise<Blob>((res, rej) => c.toBlob((b) => (b ? res(b) : rej(new Error("toBlob"))), "image/jpeg", quality));
  return { blob, width: c.width, height: c.height };
}

export async function compressImage(file: Blob, max: number, quality = 0.82): Promise<Blob> {
  return (await compressImageWithSize(file, max, quality)).blob;
}

export const plural = (n: number, one: string, few: string, many: string) => (n === 1 ? one : n >= 2 && n <= 4 ? few : many);